/**
 * OnboardingFlow - Multi-step onboarding experience
 *
 * 3-step flow: Welcome -> How It Works -> Privacy
 *
 * Features:
 * - Progress indicator (dots)
 * - Next / Skip / Get Started buttons
 * - Glassmorphic design (matches app identity)
 * - PostHog tracking for each step
 * - Marks profiles.is_onboarded on completion
 *
 * @see DD-159
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { GlassBackground } from '../glass/GlassBackground';
import { GlassCard } from '../glass/GlassCard';
import { GlassButton } from '../glass/GlassButton';
import { WelcomeStep } from './steps/WelcomeStep';
import { HowItWorksStep } from './steps/HowItWorksStep';
import { PrivacyStep } from './steps/PrivacyStep';
import { useTranslation } from '@dramadetector/shared';
import { captureEvent } from '../../lib/analytics';

const STEP_NAMES = ['welcome', 'how_it_works', 'privacy'] as const;

interface OnboardingFlowProps {
  /** Called when onboarding is completed or skipped */
  onComplete: () => void;
  /** User display name for personalized welcome */
  displayName?: string;
}

export const OnboardingFlow: React.FC<OnboardingFlowProps> = ({
  onComplete,
  displayName,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { height: windowHeight } = useWindowDimensions();
  const { t } = useTranslation('common');

  // Track onboarding start on mount
  useEffect(() => {
    captureEvent('onboarding_started', {
      total_steps: STEP_NAMES.length,
    });
    captureEvent('onboarding_step_viewed', {
      step: 0,
      step_name: STEP_NAMES[0],
    });
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < STEP_NAMES.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      captureEvent('onboarding_step_viewed', {
        step: nextStep,
        step_name: STEP_NAMES[nextStep],
      });
    } else {
      // Final step — complete onboarding
      onComplete();
    }
  }, [currentStep, onComplete]);

  const handleSkip = useCallback(() => {
    captureEvent('onboarding_skipped', {
      skipped_at_step: currentStep,
      skipped_at_step_name: STEP_NAMES[currentStep],
    });
    onComplete();
  }, [currentStep, onComplete]);

  const isLastStep = currentStep === STEP_NAMES.length - 1;

  return (
    <GlassBackground>
      <View style={[styles.container, { minHeight: windowHeight }]}>
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          bounces={false}
        >
          {/* Skip button (top-right) */}
          <View style={styles.header}>
            <Pressable
              onPress={handleSkip}
              style={styles.skipButton}
              accessibilityRole="button"
              accessibilityLabel="Skip onboarding"
              testID="onboarding-skip"
            >
              <Text style={styles.skipText}>{t('skip', { defaultValue: 'Skip' })}</Text>
            </Pressable>
          </View>

          {/* Step content */}
          <View style={styles.stepContainer}>
            <GlassCard withChromeBorder={false}>
              {currentStep === 0 && <WelcomeStep displayName={displayName} />}
              {currentStep === 1 && <HowItWorksStep />}
              {currentStep === 2 && <PrivacyStep />}
            </GlassCard>
          </View>

          {/* Progress dots */}
          <View style={styles.progressContainer}>
            {Array.from({ length: STEP_NAMES.length }).map((_, index) => (
              <View
                key={index}
                style={[
                  styles.dot,
                  index === currentStep && styles.dotActive,
                ]}
                accessibilityLabel={t('step_indicator', { current: index + 1, total: STEP_NAMES.length, defaultValue: 'Step {{current}} of {{total}}' })}
              />
            ))}
          </View>

          {/* Action button */}
          <View style={styles.actionContainer}>
            <GlassButton
              variant="primary"
              onPress={handleNext}
              testID="onboarding-next"
            >
              {isLastStep ? t('get_started', { defaultValue: 'Get Started' }) : t('next', { defaultValue: 'Next' })}
            </GlassButton>
          </View>

          {/* Step counter */}
          <Text style={styles.stepCounter}>
            {t('step_counter', { current: currentStep + 1, total: STEP_NAMES.length, defaultValue: '{{current}} of {{total}}' })}
          </Text>
        </ScrollView>
      </View>
    </GlassBackground>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: 40,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 20,
    paddingTop: Platform.OS === 'web' ? 20 : 60,
    marginBottom: 8,
  },
  skipButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    minHeight: 44, // WCAG AA touch target
  },
  skipText: {
    fontSize: 16,
    color: '#6b7280',
    fontFamily: 'Inter_500Medium',
  },
  stepContainer: {
    paddingHorizontal: 20,
    marginBottom: 32,
  },
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 32,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(168, 85, 247, 0.2)',
  },
  dotActive: {
    backgroundColor: '#a855f7',
    width: 24, // Elongated active dot
  },
  actionContainer: {
    alignItems: 'center',
    paddingHorizontal: 40,
    marginBottom: 16,
  },
  stepCounter: {
    textAlign: 'center',
    fontSize: 13,
    color: '#9ca3af',
    fontFamily: 'Inter_400Regular',
  },
});
